import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { MXN } from "../lib/apiClient";
import { TAG_LABELS, TAG_STYLE } from "../lib/constants";
import { useFavorites } from "../hooks/useFavorites";
import { Heart, ShoppingBag, ChevronLeft, ChevronRight, Star } from "lucide-react";

export const BestsellerCarousel = ({ products }) => {
  const navigate = useNavigate();
  const { isFav, toggle } = useFavorites();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", dragFree: false });
  const pausedRef = React.useRef(false);

  useEffect(() => {
    if (!emblaApi) return;
    const id = setInterval(() => { if (!pausedRef.current) emblaApi.scrollNext(); }, 3800);
    return () => clearInterval(id);
  }, [emblaApi]);

  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  if (!products || products.length === 0) return null;

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-4" data-testid="bestseller-carousel"
      onMouseEnter={() => { pausedRef.current = true; }} onMouseLeave={() => { pausedRef.current = false; }}>
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="font-serif italic text-vino/60 flex items-center gap-1"><Star className="w-4 h-4 fill-dorado text-dorado" /> los favoritos de todos</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-vino tracking-tight">Más vendidos</h2>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={prev} data-testid="carousel-prev" className="p-2.5 rounded-full bg-blancoCalido border border-beige text-vino hover:bg-beige transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={next} data-testid="carousel-next" className="p-2.5 rounded-full bg-blancoCalido border border-beige text-vino hover:bg-beige transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex -ml-4 sm:-ml-5">
          {products.map((p) => {
            const tag = p.tags?.[0]; const ts = tag ? (TAG_STYLE[tag] || { bg: "#4B1025", animated: false }) : null;
            const fav = isFav(p.id);
            return (
              <div key={p.id} className="flex-[0_0_86%] sm:flex-[0_0_48%] lg:flex-[0_0_32%] min-w-0 pl-4 sm:pl-5" data-testid={`carousel-item-${p.id}`}>
                <div className="relative rounded-[28px] overflow-hidden shadow-[0_14px_36px_rgba(118,27,56,0.14)] h-64 group">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-vinoDark/90 via-vinoDark/25 to-transparent" />
                  {tag && (
                    <span className={`absolute top-3 left-3 z-10 text-crema text-[11px] font-semibold px-3 py-1 rounded-full shadow-sm ${ts.animated ? "badge-animated" : ""}`} style={{ background: ts.bg }}>
                      <span className="badge-text">{TAG_LABELS[tag] || tag}</span>
                    </span>
                  )}
                  <button onClick={() => toggle(p.id)} data-testid={`carousel-fav-${p.id}`} className="absolute top-3 right-3 bg-crema/90 backdrop-blur rounded-full p-2 hover:scale-110 active:scale-90 transition-transform">
                    <Heart className={`w-4 h-4 ${fav ? "fill-vino text-vino" : "text-vino/60"}`} />
                  </button>
                  <div className="absolute bottom-0 inset-x-0 p-4 text-crema">
                    <h3 className="font-serif text-2xl leading-tight">{p.name}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-serif text-xl text-dorado">{p.requires_quote ? "Cotización" : `Desde ${MXN(p.base_price)}`}</span>
                      <button onClick={() => navigate(`/pedido?producto=${p.id}`)} data-testid={`carousel-pedir-${p.id}`}
                        className="bg-crema text-vinoDark rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1.5 hover:-translate-y-0.5 active:scale-95 transition-all">
                        <ShoppingBag className="w-4 h-4" /> Pedir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
